import { Component, OnInit, Output, EventEmitter, OnDestroy, HostListener } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthenticationService } from '@app/core/services/authentication.service';
import { AlertService } from '@app/core/services/alert.service';
import { Router, NavigationEnd } from '@angular/router';
import { AuthorizedUser } from '@app/core/models/authorized-user';
import { fas, faBars, faSignOutAlt, faUser, faCog, faBell, faSmileWink } from '@fortawesome/free-solid-svg-icons';
import { icon, library, IconName, Icon } from '@fortawesome/fontawesome-svg-core';
import { NotificationService } from '@app/core/services/notification.service';
import { NotificationsResult } from '@app/core/results/notification/notifications-result';
import { Notification } from '@app/core/models/notification';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  private componentDestroyed$: Subject<void> = new Subject();
  @Output() toggleMenuClick = new EventEmitter();
  notifications: Notification[] = [];
  notificationsCount!: string;
  notificationsLoading!: boolean;
  userDropdownIsOpened: boolean = false;
  notificationDropdownIsOpened: boolean = false;
  isLoggedIn$!: Observable<AuthorizedUser | null>;

  faBars = faBars;
  faSignOutAlt = faSignOutAlt;
  faUser = faUser;
  faCog = faCog;
  faBell = faBell;
  faSmileWink = faSmileWink;

  constructor(
    private authenticationService: AuthenticationService,
    private notificationService: NotificationService,
    private alertService: AlertService,
    private router: Router
  ) { }

  ngOnInit() {
    library.add(fas);
    this.isLoggedIn$ = this.authenticationService.isLoggedIn;

    this.router.events.pipe(
      takeUntil(this.componentDestroyed$)
    ).subscribe((val) => {
      // Close menu on navigation change in mobile
      if (val instanceof NavigationEnd) {
          this.setUserDropdownState(false);
      }
    });

    this.notificationService.notifications$.subscribe((notificationsResult: NotificationsResult) => {
      this.notifications = notificationsResult.notifications.edges.map((edge) => edge.node);
      this.notificationsCount = notificationsResult.notifications.totalCount.toString();   
    });

  }

  iconStringToIcon(iconName: string): Icon {
    return icon({ prefix: 'fas', iconName: iconName as IconName });
  }

  @HostListener('document:click')
  clickOutside() {
    this.setUserDropdownState(false);
    this.setNotificationDropdownState(false);
  }

  onUserDropdownClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.setUserDropdownState(null);
  }

  onNotificationDropdownClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.setNotificationDropdownState(null);
  }

  setUserDropdownState(open: boolean | null) {
    if (open === null) {
      this.userDropdownIsOpened = !this.userDropdownIsOpened;
    } else {
      this.userDropdownIsOpened = open;
    }
  }

  setNotificationDropdownState(open: boolean | null) {
    if (open === null) {
      this.notificationDropdownIsOpened = !this.notificationDropdownIsOpened;
    } else {
      this.notificationDropdownIsOpened = open;
    }

    if (this.notificationDropdownIsOpened) {
      this.notificationService.refresh();
    }
  }

  onMenuToggleClick() {
    this.toggleMenuClick.emit();
  }

  handleLogOut = (event: MouseEvent) => {
    event.preventDefault();
    this.authenticationService.logOut();
    this.alertService.success($localize `Logged out`, $localize`You has been successfully logged out.`);
    this.router.navigate(['/auth/sign-in']);
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
